import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Error as ErrorIcon } from "@mui/icons-material";

import { PatientInformingProjectionDto, PatientInformingStatus } from "../../api-client";
import { useSetAppointmentInformed } from "../../stores/Appointment/appointment.mutation";
import { computeInformingDisplay } from "../../helpers/informingDisplay";
import { formatTime, formatDateUI } from "../../helpers/dateFormat";
import { getColorForRxNumber } from "../../helpers/statusColor";
import { SmallChip, TherapyChip, VisitTypeBadge } from "../../atoms/Chip";

/** One time slot of the appointment: its times, what it delivers, and what the
 *  patient was told about it. `start`/`end` are a `Date` on the calendar path
 *  and an ISO string on the API-DTO path. */
export interface InformedDialogSlot {
  id?: string;
  start?: string | Date | null;
  end?: string | Date | null;
  rxNumber?: string | null;
  therapy?: string | null;
  informing?: PatientInformingProjectionDto | null;
}

interface SetInformedDialogProps {
  open: boolean;
  onClose: () => void;
  /** Appointment whose informing status is being set. */
  appointmentId?: string;
  /** Current appointment start, used to scope cache invalidation and measure drift. */
  appointmentStart?: Date;
  /** Current informing projection (status + the time the patient was told). */
  projection?: PatientInformingProjectionDto | null;
  /** Visit type ("Praxis" | "Haus" | "Heim") that drives the re-inform rule. */
  visitType?: string | null;
  /** Patient name shown in the header so the user knows whom they are informing. */
  patientName?: string | null;
  /** Every time slot of the appointment. */
  slots?: InformedDialogSlot[];
}

const INFORMED_VALUE = "informed";
const NOT_INFORMED_VALUE = "not-informed";

const asTime = (value?: string | Date | null): string => {
  if (!value) return "—";
  return formatTime(value instanceof Date ? value.toISOString() : value);
};

/** A slot's start time, or a marker when it has no start yet. */
const slotStartTime = (slot: InformedDialogSlot): string =>
  slot.start ? asTime(slot.start) : "nicht geplant";

/** A slot's rx number and therapy as colour-coded chips. */
const SlotChips: React.FC<{ slot: InformedDialogSlot }> = ({ slot }) => (
  <>
    {slot.rxNumber && (
      <SmallChip
        label={slot.rxNumber}
        sx={{ width: "auto", backgroundColor: getColorForRxNumber(slot.rxNumber) }}
      />
    )}
    {slot.therapy && (
      // The informing projection carries display text only — no catalog
      // id — so this chip renders the neutral fallback rather than
      // guessing a colour from the abbreviation.
      <TherapyChip therapyName={slot.therapy} colorKey={undefined} sx={{ width: "auto" }} />
    )}
  </>
);

/** Column header of the comparison grid. */
const HeaderCell: React.FC<{ label?: string; align?: "left" | "center" }> = ({
  label,
  align = "center",
}) => (
  <Typography
    variant="caption"
    color="text.secondary"
    sx={{ textAlign: align, whiteSpace: "nowrap" }}
  >
    {label ?? ""}
  </Typography>
);

/**
 * One time slot as four grid cells: its start, what it delivers, the time the
 * patient was told, and the drift between the two. Colours mirror the
 * InformingBadge so the dialog reads the same way as the badge on the
 * appointment card: within tolerance only the signed minutes show, an error icon
 * is reserved for the out-of-tolerance case.
 */
const SlotComparisonRow: React.FC<{
  slot: InformedDialogSlot;
  visitType?: string | null;
}> = ({ slot, visitType }) => {
  const informedForStart = slot.informing?.informedForStart
    ? new Date(slot.informing.informedForStart)
    : null;
  const current = slot.start ? new Date(slot.start) : null;
  const driftMinutes =
    informedForStart && current
      ? Math.round((current.getTime() - informedForStart.getTime()) / 60000)
      : null;
  const driftLabel =
    driftMinutes == null
      ? ""
      : driftMinutes === 0
        ? "0 Min"
        : `${driftMinutes > 0 ? "+" : "−"}${Math.abs(driftMinutes)} Min`;

  const display = computeInformingDisplay(slot.informing, current, visitType);

  const deviationColor =
    display.kind === "error"
      ? "error.main"
      : display.kind === "arrows"
        ? "info.main"
        : "text.secondary";

  return (
    <>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
      >
        {slotStartTime(slot)}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        <SlotChips slot={slot} />
      </Box>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        sx={{ textAlign: "center", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
      >
        {informedForStart ? asTime(informedForStart) : "—"}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.25,
        }}
      >
        {informedForStart && display.kind === "error" && (
          <ErrorIcon fontSize="small" sx={{ color: "error.main" }} />
        )}
        <Typography
          variant="body2"
          fontWeight={600}
          color={informedForStart ? deviationColor : "text.secondary"}
          sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
        >
          {informedForStart ? driftLabel : "—"}
        </Typography>
      </Box>
    </>
  );
};

/**
 * Dialog for manually setting whether the patient has been informed about an
 * appointment. Replaces the previous one-click toggle so the user explicitly
 * picks the status before saving, and surfaces the time drift between the time
 * the patient was told and the current appointment time.
 */
export const SetInformedDialog: React.FC<SetInformedDialogProps> = ({
  open,
  onClose,
  appointmentId,
  appointmentStart,
  projection,
  visitType,
  patientName,
  slots,
}) => {
  const { mutate: setAppointmentInformed, isPending } = useSetAppointmentInformed();

  const status = projection?.status;
  const isCurrentlyInformed =
    status === PatientInformingStatus.Informed ||
    status === PatientInformingStatus.InformedButChanged;

  const [informed, setInformed] = useState(isCurrentlyInformed);

  useEffect(() => {
    if (open) setInformed(isCurrentlyInformed);
  }, [open, isCurrentlyInformed]);

  const handleSave = () => {
    if (!appointmentId) return;
    setAppointmentInformed(
      { appointmentId, informed, appointmentStart },
      { onSuccess: () => onClose() },
    );
  };

  // Every time slot of the appointment, earliest first, unscheduled last. Never
  // deduplicated: one row per slot, even when two carry the same rx + therapy.
  const orderedSlots = React.useMemo(() => {
    const ms = (value?: string | Date | null) =>
      value ? new Date(value).getTime() : Number.POSITIVE_INFINITY;
    return [...(slots ?? [])].sort((a, b) => ms(a.start) - ms(b.start));
  }, [slots]);
  // One row per time slot; the appointment itself stands in when no slot was
  // resolved.
  const comparisonRows: InformedDialogSlot[] = orderedSlots.length
    ? orderedSlots
    : appointmentStart
      ? [{ start: appointmentStart, informing: projection }]
      : [];
  const hasHeader = !!patientName || !!appointmentStart || !!visitType;

  return (
    <Dialog open={open} onClose={isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Patient informieren</DialogTitle>
      <DialogContent>
        {hasHeader && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
              mt: 1,
              mb: 1,
            }}
          >
            {appointmentStart && (
              <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ whiteSpace: "nowrap", mr: 2 }}
              >
                {formatDateUI(appointmentStart)}
              </Typography>
            )}
            {patientName && (
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                {patientName}
              </Typography>
            )}
            {visitType && (
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
                <VisitTypeBadge visitType={visitType} />
              </Box>
            )}
          </Box>
        )}
        {comparisonRows.length > 0 && (
          <Box
            sx={{
              mt: 2,
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "action.hover",
              display: "grid",
              gridTemplateColumns: "auto 1fr auto auto",
              columnGap: 2,
              rowGap: 1,
              alignItems: "center",
            }}
          >
            <HeaderCell label="Aktuell" align="left" />
            <HeaderCell />
            <HeaderCell label="Informiert für" />
            <HeaderCell label="Abweichung" />
            {comparisonRows.map((slot, i) => (
              <SlotComparisonRow
                key={slot.id ?? `slot-${i}`}
                slot={slot}
                visitType={visitType}
              />
            ))}
          </Box>
        )}

        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5, mt: 1 }}>
          Status
        </Typography>
        <RadioGroup
          row
          value={informed ? INFORMED_VALUE : NOT_INFORMED_VALUE}
          onChange={(e) => setInformed(e.target.value === INFORMED_VALUE)}
        >
          <FormControlLabel value={INFORMED_VALUE} control={<Radio sx={{ backgroundColor: "transparent" }} />} label="Informiert" />
          <FormControlLabel
            value={NOT_INFORMED_VALUE}
            control={<Radio sx={{ backgroundColor: "transparent" }} />}
            label="Nicht informiert"
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!appointmentId || isPending}
          startIcon={
            isPending ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          {isPending ? "Speichern…" : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
